import { useEffect, useState } from "react";
import DowntimeCalculator from "./DowntimeCalculator";
import DowntimeIncidentsTable from "./DowntimeIncidentsTable";
import {
  Autocomplete,
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import "./Monitoring.css";
import dayjs from "dayjs";
import Page404 from "../../../Page404/Page403";
import { useSelector } from "react-redux";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import FloatingActionItems from "../../../../pages/FloatingItems/FloatingActionItems";

const assetName = [
  { label: "aladdin_v2", value: "Aladdin V2" },
  { label: "aladdin_v1", value: "Aladdin V1" },
  { label: "metabase", value: "Metabase" },
];

const tableHeadings = {
  event_date: "Date",
  down_timestamp: "Downtime Start",
  up_timestamp: "Downtime End",
  downtime_duration: "Downtime Duration ",
};

const Monitoring = () => {
  const token = useSelector(selectUserToken);
  const userData = useSelector(selectUserData);
  const hasMonitoringViewPermission = CheckPermissions(
    userData?.data?.products?.assets,
    "health",
    "view_monitoring_dashboard"
  );

  const siteOptions =
    userData?.data?.website_permissions?.map((siteObj: any) => {
      const uid = Object.keys(siteObj)[0]; // UID of the site
      const siteName = siteObj[uid].ui_site_name; // Site name
      return { uid, siteName }; // Return as an object with uid and siteName
    }) || [];

  // Merging the site options with assetName options
  const combinedOptions = [
    ...siteOptions.map((site: any) => ({
      label: site.siteName, // Display the site name
      value: site.uid, // Store the UID
      isAsset: false, // To differentiate between asset and site
    })),
    ...assetName.map((asset) => ({
      label: asset.value, // Display the asset label
      value: asset.label, // Store the asset label
      isAsset: true, // To differentiate between asset and site
    })),
  ];

  const [selectedSharedUid, setSelectedSharedUid] = useState([]) as any;
  const [selectedVersion, setSelectedVersion] = useState([]) as any;
  const [startDate, setStartDate] = useState([]) as any;
  const [endDate, setEndDate] = useState([]) as any;
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(false);
  const [dataShow, setDataShow] = useState(false);
  const [isDataAvilable, setisDataAvilable] = useState(false);
  const [isPreviousDataAvilable, setIsPreviousDataAvilable] = useState(false);
  const [tabledata, setTabledata] = useState([]);
  const [calculatorLoader, setCalculatorLoader] = useState(false);

  const handleSearch = () => {
    setTabledata([]);
    setLoading(true);
    setCalculatorLoader(true); // show loader
    setDataShow(false);
    setSearch(true);
    if (
      selectedSharedUid.length !== 0 &&
      startDate.length !== 0 &&
      endDate.length !== 0
    ) {
      const params = new URLSearchParams();
      params.append("selectedSite", selectedSharedUid); // Use site UID
      params.append("date", `${startDate},${endDate}`); // Start and end date
      const newUrl = `${
        window.location.origin
      }/operations/monitoring?${params.toString()}`;
      // Update the browser URL without reloading the page
      window.history.pushState({}, "", newUrl);
     
    }
     setDataShow(true);
  };

  useEffect(() => {
    const now = dayjs(); // Get the current date
    const startOfMonth = now.startOf("month"); // Get the first day of the current month
    const endOfMonth = now.endOf("month"); // Get the last day of the current month

    setStartDate(startOfMonth.format("YYYY-MM-DD")); // Set start date in YYYY-MM-DD format
    setEndDate(endOfMonth.format("YYYY-MM-DD")); // Set end date in YYYY-MM-DD format
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectedSite = params.get("selectedSite");
    const dateRange = params.get("date");

    if (selectedSite && dateRange) {
      const isValidUID = combinedOptions?.some(
        (option) => option.value === selectedSite
      );

      if (!isValidUID) {
        // Invalid UID, clean the URL and reset state
        const cleanUrl = `${window.location.origin}/operations/monitoring`;
        window.history.replaceState({}, "", cleanUrl);

        // Optional: reset internal state
        setSelectedSharedUid([]);
        setSelectedVersion([]);
        setDataShow(false);

        return; // Exit early to prevent further processing
      }
      setSelectedSharedUid(selectedSite);
      const dates = dateRange.split(",");
      if (dates.length === 2) {
        setStartDate(dates[0]);
        setEndDate(dates[1]);
      }
      setDataShow(true); // Show data if parameters are present
      handleSearch(); // Trigger search to fetch data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectedOption = combinedOptions.find(
    (option) => option?.value === selectedSharedUid
  );
  const resetDatesToCurrentMonth = () => {
    const now = dayjs();
    setStartDate(now.startOf("month").format("YYYY-MM-DD"));
    setEndDate(now.endOf("month").format("YYYY-MM-DD"));
  };
  return (
    <>
      {/* Monitoring Filter */}
      {hasMonitoringViewPermission ? (
        <Paper className="monitoringFiltersWrap" sx={{ padding: "16px" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={8}>
              <Typography variant="h3" sx={{ fontSize: "24px" }}>
                Monitoring
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "14px" }}>
                Analyze Website Performance Trends to Keep Your Website Running
                Smoothly
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center" pt={1}>
            <Grid item xs={4} md={6} textAlign={"end"}>
              <Autocomplete
                id="assetOrSiteName"
                disablePortal
                options={combinedOptions} // Use the combined options for Autocomplete
                getOptionLabel={(option) => option.label} // Display the label (siteName or assetName)
                fullWidth
                value={selectedOption || null}
                size="small"
                sx={{ pt: "8px" }}
                slotProps={{
                  paper: { sx: { "& .MuiAutocomplete-noOptions": { textAlign: "center" } } },
                  popper: { placement: "bottom", modifiers: [{ name: "flip", enabled: false }] },
                }}
                onChange={(_, newValue) => {
                  setDataShow(false);
                  setTabledata([]);
                  resetDatesToCurrentMonth();
                  if (!newValue) {
                    // If newValue is null, clear both selected states
                    setSelectedSharedUid([]);
                    setSelectedVersion([]);
                    setDataShow(false);
                    const newUrl = `${window.location.origin}/operations/monitoring`;
                    window.history.pushState({}, "", newUrl);
                  } else if (newValue.isAsset) {
                    // If an asset is selected, clear selectedSharedUid and set selectedVersion
                    setSelectedSharedUid([]);
                    setSelectedVersion(newValue.value);
                  } else {
                    // If a site is selected, clear selectedVersion and set selectedSharedUid
                    setSelectedVersion([]);
                    setSelectedSharedUid(newValue.value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    color="secondary"
                    {...params}
                    required
                    label="Asset"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ marginLeft: "12px" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              className="monitoringDatePicker"
              item
              xs={12}
              sm={6}
              md={2}
              textAlign={"end"}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        color: "secondary",
                        required: true,
                        fullWidth: true,
                      },
                    }}
                    format="DD/MM/YYYY"
                    value={startDate && startDate.length > 0 ? dayjs(startDate) : null}
                    sx={{ width: "100%", minWidth: "unset !important" }}
                    label="Downtime Start"
                    onError={(error) => setStartDateError(!!error)}
                    onChange={(newValue) => {
                      if (newValue) {
                        const formattedDate =
                          dayjs(newValue).format("YYYY-MM-DD");

                        // If the selected start date is later than the end date, reset it
                        if (formattedDate > endDate) {
                          // Optionally, reset the end date as well
                          setStartDate(formattedDate);
                          setEndDate(formattedDate); // Reset endDate if needed
                        } else {
                          setStartDate(formattedDate);
                        }
                      }
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>
            <Grid
              className="monitoringDatePicker"
              item
              xs={12}
              sm={6}
              md={2}
              textAlign={"end"}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        color: "secondary",
                        required: true,
                        fullWidth: true,
                      },
                    }}
                    format="DD/MM/YYYY"
                    sx={{ width: "100%", minWidth: "unset !important" }}
                    label="Downtime End"
                    disabled={!startDate || startDate.length === 0}
                    minDate={startDate && startDate.length > 0 ? dayjs(startDate) : undefined}
                    value={endDate && endDate.length > 0 ? dayjs(endDate) : null}
                    onError={(error) => setEndDateError(!!error)}
                    onChange={(newValue) => {
                      if (newValue) {
                        const formattedDate =
                          dayjs(newValue).format("YYYY-MM-DD");
                        setEndDate(formattedDate);
                      }
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={2} textAlign={"end"}>
              <Button
                data-tracker="154"
                sx={{ marginTop: "5px" }}
                size="large"
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleSearch}
                disabled={
                  startDateError ||
                  endDateError ||
                  ((selectedSharedUid.length === 0 ||
                    startDate.length === 0 ||
                    endDate.length === 0) &&
                  (selectedVersion.length === 0 ||
                    startDate.length === 0 ||
                    endDate.length === 0))
                }
              >
                Run Health Check
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <Page404 />
      )}

      {/* Monitoring Filter End*/}
      {((selectedSharedUid.length !== 0 &&
        startDate.length !== 0 &&
        endDate.length !== 0 &&
        dataShow) ||
        (selectedVersion.length !== 0 &&
          startDate.length !== 0 &&
          endDate.length !== 0 &&
          dataShow)) && (
        <DowntimeCalculator
          selectedSharedUid={selectedSharedUid}
          selectedVersion={selectedVersion}
          startDate={startDate}
          endDate={endDate}
          token={token}
          setLoading={setLoading}
          loading={loading}
          calculatorLoader={calculatorLoader}
          setCalculatorLoader={setCalculatorLoader}
          search={search}
          setSearch={setSearch}
          setisDataAvilable={setisDataAvilable}
          isDataAvilable={isDataAvilable}
          dataShow={dataShow}
          tableData={tabledata}
          setIsPreviousDataAvilable={setIsPreviousDataAvilable}
          isPreviousDataAvilable={isPreviousDataAvilable}
        />
      )}

      {(selectedSharedUid.length !== 0 &&
        startDate.length !== 0 &&
        endDate.length !== 0 &&
        dataShow) ||
      (selectedVersion.length !== 0 &&
        startDate.length !== 0 &&
        endDate.length !== 0 &&
        dataShow) ? (
        <>
          <DowntimeIncidentsTable
            tableHeadings={tableHeadings}
            selectedSharedUid={selectedSharedUid}
            selectedVersion={selectedVersion}
            startDate={startDate}
            endDate={endDate}
            token={token}
            setLoading={setLoading}
            loading={loading}
            calculatorLoader={calculatorLoader}
            setCalculatorLoader={setCalculatorLoader}
            search={search}
            setSearch={setSearch}
            setisDataAvilable={setisDataAvilable}
            isDataAvilable={isDataAvilable}
            setTabledata={setTabledata}
            tableData={tabledata}
          />
          <div data-tracker="400">
            <FloatingActionItems
              selectedSiteFloatingAction={selectedSharedUid}
              selectedDimensionsFloatingAction={""}
              trackIdFloatingAction={"430"}
            />
          </div>
        </>
      ) : (
        ""
      )}
    </>
  );
};

export default Monitoring;
